import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Layout from "@/components/layout/Layout";
import ScrollToTop from "@/components/layout/ScrollToTop";
import HomePage from "./pages/HomePage";
import ChatWidget from "@/components/ChatWidget";

// Lazy-load non-critical routes to shrink initial JS bundle
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const QuotePage = lazy(() => import("./pages/QuotePage"));
const InspectionPage = lazy(() => import("./pages/InspectionPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ChatbotStats = lazy(() => import("./pages/ChatbotStats"));
const AdminGallery = lazy(() => import("./pages/AdminGallery"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const RequireAdmin = lazy(() => import("@/components/auth/RequireAdmin"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-label="Cargando" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/sobre-nosotros" element={<AboutPage />} />
                  <Route path="/servicios" element={<ServicesPage />} />
                  <Route path="/proyectos" element={<ProjectsPage />} />
                  <Route path="/contacto" element={<ContactPage />} />
                  <Route path="/presupuesto" element={<QuotePage />} />
                  <Route path="/inspeccion-de-rocodromos" element={<InspectionPage />} />
                  <Route path="/inspeccion" element={<Navigate to="/inspeccion-de-rocodromos" replace />} />
                  {["/avis-legal", "/aviso-legal", "/aviso-legal.html", "/aviso_legal", "/avisolegal"].map((p) => (
                    <Route key={p} path={p} element={<Navigate to="/contacto" replace />} />
                  ))}
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route
                    path="/admin/chatbot-stats"
                    element={
                      <RequireAdmin>
                        <ChatbotStats />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/galeria"
                    element={
                      <RequireAdmin>
                        <AdminGallery />
                      </RequireAdmin>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
            <ChatWidget />
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
